// `marketing.subscription.ended.v1` — emitted by pricing-backend when an org's
// MARKETING entitlement ENDS: it transitions from a marketing-entitled plan
// (BASE / PLUS / PRO) down to STARTER, via EITHER a Stripe
// `customer.subscription.deleted` (cancellation reaching its period end) OR a
// `customer.subscription.updated` that downgrades to STARTER (Customer-Portal
// tier switch). See PE-1594 D19.
//
// IT IS A TRANSITION EVENT, NOT A PLAN-VALUE EVENT. Pricing emits it ONLY on the
// marketing-entitled → STARTER crossing — NOT on Plus → Base (Base keeps
// PocketFlows, so its business must be untouched), and NOT on a redelivered
// already-Starter event (the prior plan was already non-entitled → no
// transition → no emit). Checking the transition (was-marketing && now-not)
// keeps the emit idempotent against Stripe webhook redelivery.
//
// Producer: pricing-backend (`Source: 'charitystack.pricing'`), from the
//   verified Stripe webhook, fired through the existing `PocketflowSuspendPort`
//   seam (record-subscription.ts). The emit MUST throw on EventBridge
//   `FailedEntryCount > 0` so the webhook 500s and Stripe redelivers — the
//   pricing-side writes (STARTER downgrade) are idempotent and safe to repeat
//   (PE-1594 Adv C-3).
// Consumer: marketing-backend — the `teardown-marketing-business` Inngest
//   workflow (flip marketing_tier_status → READ_ONLY, stamp
//   pocketflow_business_deleted_at, DELETE the PF business).
// detailType in EventBridge: `marketing.subscription.ended.v1` (Commitment 6).
//
// Status: active
//
// Why a SEPARATE event (not a field on an existing one): the marketing teardown
// is a distinct fact ("this org's marketing ended") with a distinct
// producer/consumer pair (pricing → marketing) and its own async business
// process. It is fire-and-forget across the domain boundary, which is exactly
// EventBridge's default role (Commitment 6).

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';
import { pocketflowBusinessIdSchema } from '../marketing/merchant-account.js';

export const marketingSubscriptionEndedV1Schema = eventEnvelopeSchema.extend({
  // The PF business to tear down. The org HAS one (the producer only emits when
  // the org had entered Marketing, i.e. has a pocketflow_business_id) — but the
  // consumer workflow still no-ops + logs defensively if it ever arrives absent
  // (a malformed event), per the donation-bridge return-don't-throw precedent.
  // Carried explicitly rather than re-derived in the consumer so the teardown
  // targets exactly the business that was live at cancellation time.
  pocketflowBusinessId: pocketflowBusinessIdSchema,
});

export type MarketingSubscriptionEndedV1 = z.infer<
  typeof marketingSubscriptionEndedV1Schema
>;

export const MARKETING_SUBSCRIPTION_ENDED_V1_DETAIL_TYPE =
  'marketing.subscription.ended.v1' as const;
